import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { PencilSquare, Trash, PlusCircle, Gear, CheckCircleFill, ExclamationTriangleFill } from 'react-bootstrap-icons';
import { schoolFeeAPI } from '../services/api';
import '../CSS/SchoolFeeAmounts.css';

function SchoolFeeAmounts({ show, onHide }) {
  const [feeAmounts, setFeeAmounts] = useState([]);
  const [editingFee, setEditingFee] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [formData, setFormData] = useState({
    class: '',
    amount: '',
    academicYear: new Date().getFullYear(),
    showAddModal: false
  });
  const [toast, setToast] = useState({
    show: false,
    message: '',
    variant: 'success'
  });

  useEffect(() => {
    if (show) {
      fetchFeeAmounts();
    }
  }, [show, selectedYear]);

  const fetchFeeAmounts = async () => {
    try {
      const response = await schoolFeeAPI.getFeeAmounts({ year: selectedYear });
      if (response.data.success) {
        setFeeAmounts(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching fee amounts:', error);
      showToastMessage('Error fetching fee amounts', 'danger');
    }
  };

  const showToastMessage = (message, variant = 'success') => {
    setToast({ show: true, message, variant });
    setTimeout(() => {
      setToast(prev => ({ ...prev, show: false }));
    }, 3000);
  };

  const handleShowEditModal = (fee = null) => {
    if (fee) {
      setEditingFee(fee);
      setFormData({ class: fee.class, amount: fee.amount.toString(), academicYear: fee.academic_year, showAddModal: false });
    } else {
      setEditingFee(null);
      setFormData({ class: '', amount: '', academicYear: selectedYear, showAddModal: true });
    }
  };

  const handleCloseEditModal = () => {
    setEditingFee(null);
    setFormData({ class: '', amount: '', academicYear: selectedYear, showAddModal: false });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingFee) {
        const response = await schoolFeeAPI.updateFeeAmount(editingFee.id, {
          class: formData.class,
          amount: parseFloat(formData.amount),
          academicYear: formData.academicYear
        });
        if (response.data.success) {
          showToastMessage('Fee amount updated successfully');
          fetchFeeAmounts();
          handleCloseEditModal();
        }
      } else {
        const response = await schoolFeeAPI.createFeeAmount({
          class: formData.class,
          amount: parseFloat(formData.amount),
          academicYear: formData.academicYear
        });
        if (response.data.success) {
          showToastMessage('Fee amount created successfully');
          fetchFeeAmounts();
          handleCloseEditModal();
        }
      }
    } catch (error) {
      console.error('Error saving fee amount:', error);
      showToastMessage(`Error: ${error.response?.data?.message || error.message}`, 'danger');
    }
  };

  const handleDelete = async (id, className) => {
    if (window.confirm(`Are you sure you want to delete the fee amount for class ${className}?`)) {
      try {
        const response = await schoolFeeAPI.deleteFeeAmount(id);
        if (response.data.success) {
          showToastMessage('Fee amount deleted successfully');
          fetchFeeAmounts();
        }
      } catch (error) {
        console.error('Error deleting fee amount:', error);
        showToastMessage(`Error: ${error.response?.data?.message || error.message}`, 'danger');
      }
    }
  };

  return (
    <>
      {/* ══════════════════════════════════════════
          Main — Manage Fee Amounts Modal
      ══════════════════════════════════════════ */}
      <Modal show={show} onHide={onHide} size="lg">
        <Modal.Header closeButton className="sfa-modal-header">
          <Modal.Title className="sfa-modal-title">
            <Gear className="sfa-title-icon" />
            Manage School Fee Amounts
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="sfa-modal-body">
          {/* Toolbar */}
          <div className="sfa-toolbar">
            <p className="sfa-toolbar-label">Configure fee amounts for each class</p>
            <select
              className="sfa-input"
              style={{ maxWidth: '120px', marginLeft: 'auto', marginRight: '1rem' }}
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            >
              {[selectedYear - 1, selectedYear, selectedYear + 1, selectedYear + 2].map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
            <button className="sfa-btn sfa-btn-primary" onClick={() => handleShowEditModal()}>
              <PlusCircle className="sfa-btn-icon" />
              Add Fee Amount
            </button>
          </div>

          {/* Table */}
          <div className="sfa-table-wrapper">
            <table className="sfa-table">
              <thead className="sfa-table-header">
                <tr>
                  <th>Class</th>
                  <th>Year</th>
                  <th>Amount (K)</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody className="sfa-table-body">
                {feeAmounts.map((fee) => (
                  <tr key={fee.id}>
                    <td>Class {fee.class}</td>
                    <td>{fee.academic_year}</td>
                    <td>
                      {typeof fee.amount === 'number'
                        ? fee.amount.toFixed(2)
                        : parseFloat(fee.amount || 0).toFixed(2)}
                    </td>
                    <td>
                      <div className="sfa-cell-actions">
                        <button
                          className="sfa-action-btn sfa-action-edit"
                          onClick={() => handleShowEditModal(fee)}
                          title="Edit"
                        >
                          <PencilSquare />
                        </button>
                        <button
                          className="sfa-action-btn sfa-action-delete"
                          onClick={() => handleDelete(fee.id, fee.class)}
                          title="Delete"
                        >
                          <Trash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {feeAmounts.length === 0 && (
                  <tr>
                    <td colSpan="4" className="sfa-empty-state">
                      No fee amounts configured for {selectedYear}. Click "Add Fee Amount" to get started.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Modal.Body>

        <Modal.Footer className="sfa-modal-footer">
          <button className="sfa-btn sfa-btn-secondary" onClick={onHide}>
            Close
          </button>
        </Modal.Footer>
      </Modal>

      {/* ══════════════════════════════════════════
          Edit / Add Fee Amount Modal
      ══════════════════════════════════════════ */}
      <Modal
        show={!!editingFee || formData.showAddModal}
        onHide={handleCloseEditModal}
      >
        <Modal.Header closeButton className="sfa-modal-header">
          <Modal.Title className="sfa-modal-title">
            {editingFee ? 'Edit Fee Amount' : 'Add Fee Amount'}
          </Modal.Title>
        </Modal.Header>

        <form onSubmit={handleSubmit}>
          <Modal.Body className="sfa-modal-body">

            {/* Class field */}
            <div className="sfa-form-group">
              <label className="sfa-label">Class *</label>
              <select
                className={`sfa-input${editingFee ? ' sfa-input-disabled' : ''}`}
                value={formData.class || ''}
                onChange={(e) => setFormData({ ...formData, class: e.target.value })}
                required
                disabled={!!editingFee}
              >
                <option value="">Select Class</option>
                {['6', '7', '8', '9', '10', '11', '12'].map(cls => (
                  <option key={cls} value={cls}>Class {cls}</option>
                ))}
              </select>
              {editingFee && (
                <p className="sfa-field-hint">
                  Class cannot be changed when editing an existing fee amount.
                </p>
              )}
            </div>

            {/* Academic Year field */}
            <div className="sfa-form-group">
              <label className="sfa-label">Academic Year *</label>
              <input
                type="number"
                className={`sfa-input${editingFee ? ' sfa-input-disabled' : ''}`}
                value={formData.academicYear}
                onChange={(e) => setFormData({ ...formData, academicYear: parseInt(e.target.value) })}
                disabled={!!editingFee}
                required
              />
              {editingFee && (
                <p className="sfa-field-hint">
                  Year cannot be changed when editing an existing fee amount.
                </p>
              )}
            </div>

            {/* Amount field */}
            <div className="sfa-form-group">
              <label className="sfa-label">Amount (K) *</label>
              <input
                type="number"
                step="0.01"
                min="0"
                className="sfa-input"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                placeholder="Enter fee amount"
                required
              />
            </div>

          </Modal.Body>

          <Modal.Footer className="sfa-modal-footer">
            <button type="button" className="sfa-btn sfa-btn-secondary" onClick={handleCloseEditModal}>
              Cancel
            </button>
            <button type="submit" className="sfa-btn sfa-btn-primary">
              {editingFee ? 'Update' : 'Create'}
            </button>
          </Modal.Footer>
        </form>
      </Modal>

      {/* ══════════════════════════════════════════
          Toast
      ══════════════════════════════════════════ */}
      <div className={`sfa-toast sfa-toast-${toast.variant}${toast.show ? ' sfa-toast-show' : ''}`}>
        <div className="sfa-toast-header">
          <strong className="sfa-toast-title">
            {toast.variant === 'success' ? 'Success' : 'Error'}
          </strong>
          <button
            className="sfa-toast-close"
            onClick={() => setToast(prev => ({ ...prev, show: false }))}
          >
            ×
          </button>
        </div>
        <div className="sfa-toast-body">
          {toast.variant === 'success'
            ? <CheckCircleFill className="sfa-toast-icon" />
            : <ExclamationTriangleFill className="sfa-toast-icon" />}
          {toast.message}
        </div>
      </div>
    </>
  );
}

export default SchoolFeeAmounts;